import { useEffect, useMemo } from 'react'

import type { IAnnotationEngine } from '@/features/annotation'
import { useCommentStore } from '@/features/view-tfl-uploaded/store'

interface UseAnnotationHighlightsOptions {
  documentVersionId?: string
  enabled?: boolean
  engine: IAnnotationEngine | null
}

export const useAnnotationHighlights = ({
  documentVersionId,
  enabled = true,
  engine,
}: UseAnnotationHighlightsOptions) => {
  const { commentsByDocVersion, annotationsByDocVersion, activeCommentId } =
    useCommentStore()

  const commentsById = useMemo(() => {
    if (!documentVersionId) return {}
    return commentsByDocVersion[documentVersionId] ?? {}
  }, [commentsByDocVersion, documentVersionId])

  const annotationsByCommentId = useMemo(() => {
    if (!documentVersionId) return {}
    return annotationsByDocVersion[documentVersionId] ?? {}
  }, [annotationsByDocVersion, documentVersionId])

  const comments = useMemo(() => Object.values(commentsById), [commentsById])
  const isLoading = false

  useEffect(() => {
    if (!enabled || !documentVersionId || isLoading || !engine) {
      return
    }

    engine.removeAll()

    if (!activeCommentId) {
      return
    }

    const activeComment = commentsById[activeCommentId]
    const activeAnnotation = annotationsByCommentId[activeCommentId]

    if (!activeComment || !activeAnnotation || !activeComment.annotation) {
      return
    }

    engine.apply(activeComment.annotation, activeCommentId)

    return () => {
      if (engine) {
        engine.removeAll()
      }
    }
  }, [
    commentsById,
    annotationsByCommentId,
    activeCommentId,
    isLoading,
    enabled,
    documentVersionId,
    engine,
  ])

  return {
    isLoading,
    comments,
  }
}
